/**
 * @fileoverview VideoTrack component for rendering LiveKit video tracks with adaptive sizing.
 * 
 * This component handles the display of video elements from LiveKit track publications,
 * automatically adjusting sizing based on the video's aspect ratio. It attaches LiveKit
 * tracks to HTML video elements and provides callbacks for video lifecycle events.
 * 
 * Key Features:
 * - Automatic aspect ratio detection and adaptive sizing
 * - LiveKit track attachment to video elements
 * - Video dimension change detection (e.g., phone rotation)
 * - Optional track SID overlay for debugging
 * - Comprehensive error handling and logging
 * 
 * Sizing Logic:
 * - Landscape videos (aspect >= 1): Height 100%, width auto
 * - Portrait videos (aspect < 1): Height 100%, width auto with maxWidth constraint
 * - Loading state: Fill container with object-fit contain
 * 
 * @module VideoTrack
 */
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRecordingContext } from '../RecordingContext';
import { useLogger } from '@ui/shared/components/Logger';
import { LOGGER_LABEL } from '../utils/constants';

/**
 * VideoTrack component renders a video element with the LiveKit track attached.
 * 
 * This component manages the video element lifecycle, including track attachment,
 * aspect ratio calculation, and dimension change detection. It automatically adjusts
 * the video sizing based on the stream's aspect ratio to ensure proper display.
 *
 * @param {object} props - Component props
 * @param {object} props.trackRef - LiveKit track reference containing:
 *   - participant: Participant information
 *   - publication: Track publication with track and trackSid
 * @param {React.RefObject} props.videoRef - Ref forwarded to video element for external access
 *   (used by TrackWrapper for position measurement via getBoundingClientRect)
 * @param {object} props.style - Base CSS styles to apply to video element
 *   (will be merged with computed aspect ratio styles)
 * @param {function} props.onVideoLoaded - Callback invoked when video is ready:
 *   - Called on loadedMetadata event (initial load)
 *   - Called on resize event (dimension changes)
 *   - Called on playing event (playback starts)
 * @param {string} props.dataTrackSid - Track SID for data-track-sid attribute
 *   (used for debugging and optional overlay display)
 * @returns {React.ReactElement} Video element with track attached, or placeholder div if track unavailable
 */
const VideoTrack = ({
  trackRef,
  videoRef,
  style,
  onVideoLoaded,
  dataTrackSid,
}) => {
  /**
   * Current aspect ratio of the video stream (width / height).
   * Used to determine optimal sizing strategy (landscape vs portrait).
   * @type {number|null}
   */
  const [aspect, setAspect] = useState(null);
  const { showTrackSid } = useRecordingContext();
  const logger = useLogger();

  // Log component mount/unmount
  useEffect(() => {
    logger.debug(LOGGER_LABEL, 'VideoTrack mounted', {
      trackSid: dataTrackSid,
      hasTrackRef: !!trackRef,
      hasPublication: !!trackRef?.publication
    });

    return () => {
      logger.debug(LOGGER_LABEL, 'VideoTrack unmounted', {
        trackSid: dataTrackSid
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handles the loadedMetadata event when video metadata becomes available.
   * 
   * Extracts video dimensions (videoWidth, videoHeight) and calculates the aspect
   * ratio to determine optimal sizing strategy. This is the first event that fires
   * when the video stream is ready and dimensions are known.
   * 
   * @function handleLoadedMetadata
   * @param {Event} e - The loadedMetadata event from the video element
   * @param {HTMLVideoElement} e.target - Video element with videoWidth and videoHeight properties
   * @private
   */
  const handleLoadedMetadata = useCallback((e) => {
    try {
      if (e.target && e.target.videoWidth && e.target.videoHeight) {
        const aspectRatio = e.target.videoWidth / e.target.videoHeight;
        setAspect(aspectRatio);

        logger.debug(LOGGER_LABEL, 'Video metadata loaded', {
          trackSid: dataTrackSid,
          width: e.target.videoWidth,
          height: e.target.videoHeight,
          aspectRatio: aspectRatio.toFixed(2)
        });
      } else {
        logger.warn(LOGGER_LABEL, 'Video metadata loaded but dimensions missing', {
          trackSid: dataTrackSid,
          hasTarget: !!e.target,
          hasWidth: !!e.target?.videoWidth,
          hasHeight: !!e.target?.videoHeight
        });
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error handling video metadata', {
        error,
        errorMessage: error.message,
        trackSid: dataTrackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTrackSid]);

  /**
   * Handles the resize event when video dimensions change.
   * 
   * Detects changes in video stream dimensions (e.g., when a phone rotates from
   * portrait to landscape or vice versa). Recalculates aspect ratio and triggers
   * the onVideoLoaded callback to update position measurements in TrackWrapper.
   * 
   * @function handleResize
   * @param {Event} e - The resize event from the video element
   * @param {HTMLVideoElement} e.target - Video element with updated videoWidth and videoHeight
   * @private
   */
  const handleResize = useCallback((e) => {
    try {
      if (e.target && e.target.videoWidth && e.target.videoHeight) {
        const newAspect = e.target.videoWidth / e.target.videoHeight;
        const oldAspect = aspect;

        setAspect(newAspect);

        logger.info(LOGGER_LABEL, 'Video dimensions changed', {
          trackSid: dataTrackSid,
          oldAspect: oldAspect?.toFixed(2),
          newAspect: newAspect.toFixed(2),
          width: e.target.videoWidth,
          height: e.target.videoHeight
        });

        // Trigger the onVideoLoaded callback to update position measurements
        if (onVideoLoaded) {
          try {
            onVideoLoaded();
          } catch (callbackError) {
            logger.error(LOGGER_LABEL, 'Error in onVideoLoaded callback', {
              error: callbackError,
              errorMessage: callbackError.message,
              trackSid: dataTrackSid
            });
          }
        }
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error handling video resize', {
        error,
        errorMessage: error.message,
        trackSid: dataTrackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onVideoLoaded, aspect, dataTrackSid]);

  /**
   * Guard clause: Return placeholder if track reference is invalid.
   * This prevents errors when track is not yet available or has been removed.
   */
  if (!trackRef || !trackRef.publication) {
    logger.warn(LOGGER_LABEL, 'VideoTrack rendering placeholder - missing trackRef or publication', {
      trackSid: dataTrackSid,
      hasTrackRef: !!trackRef,
      hasPublication: !!trackRef?.publication
    });
    return <div style={style || {}} className="video-placeholder"></div>;
  }

  /**
   * Calculate video element styles based on aspect ratio.
   * 
   * Sizing Strategy:
   * - Landscape (aspect >= 1): Set height to 100%, let width be auto
   * - Portrait (aspect < 1): Set height to 100%, width auto with maxWidth constraint
   * - Loading (aspect null): Fill container with object-fit contain
   * 
   * All videos use object-fit: contain to maintain aspect ratio without cropping.
   */
  let videoStyle = { ...style };
  if (aspect) {
    if (aspect >= 1) {
      // Wider than tall
      videoStyle.width = 'auto';
      videoStyle.height = '100%';
      videoStyle.maxHeight = '100%';
    } else {
      // Taller than wide
      videoStyle.width = 'auto';
      videoStyle.height = '100%';
      videoStyle.maxWidth = '100%';
    }
    videoStyle.objectFit = 'contain';
    videoStyle.display = 'block';
    videoStyle.margin = '0 auto';
  } else {
    // Fallback to fill container while loading
    videoStyle.width = '100%';
    videoStyle.height = '100%';
    videoStyle.objectFit = 'contain';
    videoStyle.display = 'block';
    videoStyle.margin = '0 auto';
  }

  return (
    <div
      data-track-sid={dataTrackSid}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      <video
        key={trackRef.publication.trackSid}
        /**
         * Video element ref callback.
         * 
         * Performs two critical operations:
         * 1. Assigns element to videoRef for external access (position measurement)
         * 2. Attaches LiveKit track to video element using track.attach()
         * 
         * The attachment check prevents redundant attachments by comparing srcObject.
         * 
         * @param {HTMLVideoElement} el - The video element
         */
        ref={(el) => {
          try {
            // Assign to the measurement ref
            if (videoRef) {
              videoRef.current = el;
            }

            // Attach the LiveKit track
            if (el && trackRef.publication.track) {
              if (el.srcObject !== trackRef.publication.track.mediaStreamTrack) {
                try {
                  trackRef.publication.track.attach(el);
                  logger.debug(LOGGER_LABEL, 'Track attached to video element', {
                    trackSid: dataTrackSid,
                    trackKind: trackRef.publication.track.kind
                  });
                } catch (attachError) {
                  logger.error(LOGGER_LABEL, 'Failed to attach track to video element', {
                    error: attachError,
                    errorMessage: attachError.message,
                    trackSid: dataTrackSid
                  });
                }
              }
            } else if (el && !trackRef.publication.track) {
              logger.warn(LOGGER_LABEL, 'Video element ready but track not available', {
                trackSid: dataTrackSid,
                hasPublication: !!trackRef.publication
              });
            }
          } catch (error) {
            logger.error(LOGGER_LABEL, 'Error in video ref callback', {
              error,
              errorMessage: error.message,
              trackSid: dataTrackSid
            });
          }
        }}
        style={videoStyle}
        muted
        autoPlay
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        onResize={handleResize}
        onPlaying={onVideoLoaded}
      />
      {/* Overlay for trackId - only shown if showTrackSid is true */}
      {dataTrackSid && showTrackSid && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'rgba(255,255,255,0.95)',
            color: '#000',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 14,
            fontWeight: 600,
            zIndex: 10,
            pointerEvents: 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
            fontFamily: 'monospace',
            letterSpacing: 0.5,
          }}
        >
          {dataTrackSid}
        </div>
      )}
    </div>
  );
};

/**
 * PropTypes validation for VideoTrack component.
 * 
 * @property {object} trackRef - Optional. LiveKit track reference with participant and publication
 * @property {object} videoRef - Optional. React ref to be assigned to video element
 * @property {object} style - Optional. Base CSS styles for video element
 * @property {function} onVideoLoaded - Optional. Callback for video ready events
 * @property {string} dataTrackSid - Optional. Track SID for data attribute and overlay
 */
VideoTrack.propTypes = {
  trackRef: PropTypes.object,
  videoRef: PropTypes.object,
  style: PropTypes.object,
  onVideoLoaded: PropTypes.func,
  dataTrackSid: PropTypes.string,
};

export default VideoTrack;
